import { useMemo, useRef, useState } from "react";
import { Check, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Pager } from "@/components/ui/pager";
import { MODAL_PAGE_SIZE } from "@/lib/paging";
import { cn } from "@jbnl/shared-ui";

/**
 * A list of records to choose one of, inside a dialog. Not a Select, which ADR-0028 keeps for a
 * fixed vocabulary; searches past a threshold because §4.4.4 makes every persoon a candidate.
 */
export function Keuzelijst<T>({
  naam,
  zoekPlaceholder,
  items,
  sleutel,
  zoekterm,
  gekozen,
  onKies,
  regel,
  leeg,
}: {
  naam: string;
  /** The search box hint for `aantal` items; the caller's `t` picks the plural form. */
  zoekPlaceholder: (aantal: number) => string;
  items: readonly T[];
  sleutel: (item: T) => string;
  /** What typing in the search box matches against. Omit to leave the list unsearchable. */
  zoekterm?: (item: T) => string;
  gekozen: string;
  onKies: (id: string) => void;
  regel: (item: T) => React.ReactNode;
  leeg: React.ReactNode;
}) {
  const { t } = useTranslation("planning");
  const [zoek, setZoek] = useState("");
  const [pagina, setPagina] = useState(1);
  const wortel = useRef<HTMLDivElement>(null);

  const zichtbaar = useMemo(() => {
    const term = zoek.trim().toLowerCase();

    if (!zoekterm || term === "") {
      return items;
    }

    return items.filter((item) => zoekterm(item).toLowerCase().includes(term));
  }, [items, zoek, zoekterm]);

  // A page of what a search narrowed - so a search that leaves one match does not still show a
  // pager counting up to the whole (unfiltered) list.
  const pageItems = zichtbaar.slice(
    (pagina - 1) * MODAL_PAGE_SIZE,
    pagina * MODAL_PAGE_SIZE,
  );

  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">{leeg}</p>;
  }

  // Ten is where scanning stops being faster than typing. Below it a search box is furniture.
  const zoekbaar = Boolean(zoekterm) && items.length > 10;

  function kies(id: string) {
    onKies(id);
    naarBoven(wortel.current);
  }

  return (
    <div ref={wortel} className="space-y-2">
      {zoekbaar && (
        <div className="relative">
          <Search
            aria-hidden
            className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 opacity-60"
          />
          <Input
            type="search"
            value={zoek}
            onChange={(event) => {
              setZoek(event.target.value);
              // A search narrows the set the pager counts, so page 2 of the old set would
              // otherwise land past the end of a shorter one.
              setPagina(1);
            }}
            placeholder={zoekPlaceholder(items.length)}
            aria-label={t("keuzelijst.zoekLabel", { naam })}
            className="ps-8"
          />
        </div>
      )}

      <div role="radiogroup" aria-label={naam} className="space-y-1">
        {zichtbaar.length === 0 && (
          <p className="px-2 py-1 text-sm text-muted-foreground">
            {t("keuzelijst.nietsGevonden")}
          </p>
        )}

        {pageItems.map((item) => {
          const id = sleutel(item);
          const actief = id === gekozen;

          return (
            <label
              key={id}
              className={cn(
                "flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1.5 text-sm",
                "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring",
                actief ? "border-brand-blue bg-accent" : "hover:bg-accent",
              )}
            >
              <input
                type="radio"
                name={naam}
                value={id}
                checked={actief}
                onChange={() => kies(id)}
                className="sr-only"
              />
              <Check
                aria-hidden
                className={cn(
                  "size-4 shrink-0",
                  actief ? "text-brand-blue" : "opacity-0",
                )}
              />
              <span className="min-w-0 flex-1">{regel(item)}</span>
            </label>
          );
        })}
      </div>

      <Pager
        page={pagina}
        pageSize={MODAL_PAGE_SIZE}
        totalCount={zichtbaar.length}
        onPageChange={setPagina}
        variant="compact"
      />
    </div>
  );
}

/**
 * Walked up the DOM because this list cannot know what it scrolls inside of; its own box is
 * skipped, or the tick just set scrolls away. Jumps: a glide loses to the click's own focus scroll.
 */
function naarBoven(vanaf: HTMLElement | null) {
  for (let el = vanaf?.parentElement; el; el = el.parentElement) {
    const { overflowY } = getComputedStyle(el);

    if (overflowY === "auto" || overflowY === "scroll") {
      el.scrollTop = 0;
      return;
    }
  }
}
